import React, { type ReactNode, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useParams } from 'react-router-dom';
import { t } from '@lingui/core/macro';
import moment from 'moment';

import { schema } from '@rover/components/src/microdata';
import OverflowTextModal from '@rover/components/src/OverflowTextModal/OverflowTextModal';
import PetBlock from '@rover/components/src/PetBlock';
import useShortlist from '@rover/components/src/shortlist/hooks/useShortlist';
import StaticLocation from '@rover/components/src/StaticLocation';
import {
  DEFAULT_PRINTABLE_BOOKING_FEE_PERCENTAGE,
  ServiceType,
} from '@rover/constants/src/translations/service.constants';
import {
  type SearchParamsPetCounts,
  FLOATING_FOOTER_PADDING,
  MOBILE_MAX_WIDTH_LAYOUT,
  SearchParamsPetType,
  SITTER_PROFILE_EVENTSTREAM_EVENTS,
} from '@rover/constants/src/translations/sitterProfilePage.constants';
import { Box, Display, Flex, Heading, Paragraph } from '@rover/kibble/core';
import Banner from '@rover/kibble/official-patterns/Banner';
import Section from '@rover/kibble/patterns/Section';
import MQ, { TABLET_MAX } from '@rover/kibble/styles/MediaQuery';
import useTheme from '@rover/kibble/themes/useTheme';
import { ROLLOUT_STAY_MODERATION } from '@rover/react-lib/src/components/ImageGallery/constants';
import ImageGallery from '@rover/react-lib/src/components/ImageGallery/ImageGallery';
import type {
  GalleryVideoItem,
  PaginationConfig,
} from '@rover/react-lib/src/components/ImageGallery/ImageGallery.common';
import {
  GalleryViewMode,
  PhotoRelationship,
} from '@rover/react-lib/src/components/ImageGallery/ImageGallery.common';
import insertIntroVideoItem, {
  INTRO_VIDEO_SLOT_INDEX,
} from '@rover/react-lib/src/components/ImageGallery/insertIntroVideoItem';
import OwnershipTag from '@rover/react-lib/src/components/ImageGallery/OwnershipTag';
import PhotoGallery from '@rover/react-lib/src/components/images/PhotoGallery';
import {
  DEFAULT_IMAGE_OFFSET,
  GALLERY_PREVIEW_IMAGE_COUNT,
} from '@rover/react-lib/src/components/images/PhotoGallery/constants';
import FloatingFooter from '@rover/react-lib/src/components/utils/FloatingFooter/FloatingFooter';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import SmartCollarModal from '@rover/react-lib/src/pages/contact-more-sitters/pages/PostRequestPage/SmartCollarModal';
import { Entrypoint as SmartCollarEntrypoint } from '@rover/react-lib/src/pages/contact-more-sitters/pages/PostRequestPage/SmartCollarModal/types';
import { useApiBrightHorizonsSitterProfileBannerRetrieve } from '@rover/rsdk/src/apiClient/latest/api-bright-horizons-sitter-profile-banner-retrieve';
import { useApiPersonFullSitterProfileRetrieve } from '@rover/rsdk/src/apiClient/latest/api-person-full-sitter-profile-retrieve';
import { useApiPersonProfileCitySeoRetrieve } from '@rover/rsdk/src/apiClient/latest/api-person-profile-city-seo-retrieve';
import { EnhancedPersonSitterProfileService } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileService';
import { Image } from '@rover/rsdk/src/apiClient/latest/model/image';
import { Pet } from '@rover/rsdk/src/apiClient/latest/model/pet';
import { UnitEnum } from '@rover/rsdk/src/apiClient/latest/model/unitEnum';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { useCurrentCountryConfiguration } from '@rover/rsdk/src/modules/Data/Core/CountryConfiguration';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';
import { Logger } from '@rover/rsdk/src/modules/Logging';
import { useGateValue } from '@rover/rsdk/src/modules/Statsig';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import useBrandSpecificSettings from '@rover/shared/js/hooks/useBrandSpecificSettings';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';
import useIsBHUser from '@rover/shared/js/hooks/useIsBHUser';
import getBrandSiteName from '@rover/shared/js/utils/getBrandSiteName';
import getBrandSpecificValue from '@rover/shared/js/utils/getBrandSpecificValue';
import {
  PriceTransparencyLevel,
  PriceTransparencyLevels,
} from '@rover/types/src/PriceTransparencyLevel';
import getDateTimeFormatMapForLocale from '@rover/utilities/datetime';
import emitMetric from '@rover/utilities/emitMetric';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';
import {
  isCatOnlyAvailablePetTypesBasedOnPetTypes,
  isCatOnlyServiceBasedOnAcceptedPetTypes,
} from '@rover/utilities/getAvailablePetTypes/availablePetTypesUtils';
import {
  coercePriceTransparencyLevel,
  getHigherPriceTransparencyLevel,
  getIsPriceTransparency,
  getIsPriceTransparencyCondensed,
} from '@rover/utilities/priceTransparency';

import MemberProfileSignal from '../../../components/RoverProtectSignals/MemberProfileSignal';
import useMatchMedia from '../../../hooks/useMatchMedia';
import BackToSearchButton from '../components/BackToSearchButton';
import ContactButton from '../components/ContactButton';
import FavoriteButton from '../components/FavoriteButton';
import Tabs from '../components/Tabs';

import AboutSection from './components/AboutSection/AboutSection';
import AvailabilityCalendarSection from './components/AvailabilityCalendarSection/AvailabilityCalendarSection';
import { BHSitterProfileBanner } from './components/BHSitterProfileBanner/BHSitterProfileBanner';
import CatCareExperienceContainer from './components/CatCareExperienceContainer/CatCareExperienceContainer';
import DjangoMessages from './components/DjangoMessages';
import GalleryBlock from './components/GalleryBlock/GalleryBlock';
import MoreSitters from './components/MoreSitters/MoreSitters';
import PromoteProfileButton from './components/PromoteProfileButton';
import ReportProfileSection from './components/ReportProfileSection/ReportProfileSection';
import RevampPageContent from './components/RevampPageContent';
import RevampProfileHeader from './components/RevampProfileHeader/RevampProfileHeader';
import { RevampProfileProvider } from './components/RevampProfileProvider';
import RevampMobileCtaBar from './components/RevampServicesCta/RevampMobileCtaBar';
import ReviewsSection from './components/ReviewsSection/ReviewsSection';
import SeoBreadcrumbs from './components/SeoBreadcrumbs/SeoBreadcrumbs';
import ServiceSection from './components/ServiceSection/ServiceSection';
import SitterInformation from './components/SitterInformation/SitterInformation';
import { SitterPriceBanner } from './components/SitterPriceBanner';
import SitterProfileLocalBusinessSchema from './components/SitterProfileLocalBusinessSchema';
import StarSitterCallout from './components/StarSitterCallout/StarSitterCallout';
import { type SitterProfileVersion, type ViewMode } from './hooks/useRevampProfileContext';
import useServiceAreaMap from './hooks/useServiceAreaMap';
import { sitterProfileDataPrefetch } from './utils/prefetch';
import { sendSectionInteractionEvent } from './utils/sendSectionInteractionEvent';
import useDelayedTractiveDiscountCodeRetrieve from './utils/useDelayedTractiveDiscountCodeRetrieve';
import useGalleryImages, { GALLERY_IMAGE_PAGE_SIZE } from './utils/useGalleryImages';
import { shouldShowRevampLayout } from './utils/utils';
import {
  SitterProfileAvatarClickedEvent,
  SitterProfileGalleryClickedEvent,
  SitterProfileGalleryImagesLoadedEvent,
  SitterProfileViewGalleryClickedEvent,
} from './analyticsEvents';
import { SitterProfileVersionOptions } from './constants';
import {
  AboutLayout,
  AvailabilityLayout,
  GalleryLayout,
  HeaderLayout,
  LocationLayout,
  MoreSittersLayout,
  PageLayout as SitterProfilePageLayout,
  PetsLayout,
  PricingAvailabilityLayout,
  ProfileLayout,
  ServiceLayout,
  SkillsAndRestDesktopLayout,
  SkillsAndRestTableLayout,
} from './SitterProfileLayout';

export type MatchParams = {
  slug?: string;
};

export const SERVICE_SLUG_PRIORITY: ServiceType[] = [
  ServiceType.OVERNIGHT_BOARDING,
  ServiceType.DROP_IN,
  ServiceType.OVERNIGHT_TRAVELING,
  ServiceType.DOG_WALKING,
  ServiceType.DOGGY_DAY_CARE,
];

const LIGHTBOX_INTRO_VIDEO_SLOT_INDEX = INTRO_VIDEO_SLOT_INDEX + DEFAULT_IMAGE_OFFSET;

export const getDefaultServiceSlug = (
  urlServiceType: string | undefined,
  services: EnhancedPersonSitterProfileService[]
): string | undefined => {
  if (urlServiceType) return urlServiceType;
  const available = services.filter((s) => !s.isAway);
  for (const slug of SERVICE_SLUG_PRIORITY) {
    if (available.some((s) => s.slug === slug)) {
      return slug;
    }
  }
  return available[0]?.slug;
};

const SitterProfilePage = (): JSX.Element => {
  const { slug = '' } = useParams<MatchParams>();
  const location = useLocation();
  const theme = useTheme();
  const displaySize = getBrandSpecificValue({
    rover: '400',
    catInAFlat: '500',
  });
  const { locale } = useI18nContext();
  const availablePets = useBrandSpecificPets();
  const isCatOnlyBrand = isCatOnlyAvailablePetTypesBasedOnPetTypes(availablePets);
  const isSmDown = useMatchMedia(MQ.SM_DOWN.getBreakpointString());
  const isDesktopSize = useMatchMedia(`(min-width:${TABLET_MAX + 1}px)`);
  const searchParams = new URLSearchParams(location.search);
  const serviceParam = searchParams.get('service_type');
  const params = serviceParam ? { service: serviceParam } : undefined;
  const nocache = searchParams.get('no_cache');
  const nocacheParams = nocache ? { no_cache: '1' } : undefined;
  const { data: sitterInfo } = useApiPersonFullSitterProfileRetrieve({
    slug,
    ...params,
    ...nocacheParams,
  });
  const currentUser = useCurrentUser();
  const { isVisible: isShortlistVisible } = useShortlist();
  const isBrightHorizonsUser = useIsBHUser();
  const { enableSitterProfileRichResults } = useBrandSpecificSettings();
  const { urls: countryConfigUrls, data: countryConfigData } = useCurrentCountryConfiguration();
  const bookingFeePercentage =
    countryConfigData?.bookingFeePercentage ?? DEFAULT_PRINTABLE_BOOKING_FEE_PERCENTAGE;
  const DF = getDateTimeFormatMapForLocale(locale);
  const [showTractiveModal, setShowTractiveModal] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryViewMode, setGalleryViewMode] = useState<GalleryViewMode>(GalleryViewMode.Lightbox);
  const [selectedGalleryImageIndex, setSelectedGalleryImageIndex] = useState(0);
  const [isFloatingFooterVisible, setIsFloatingFooterVisible] = useState(false);

  const { current: currentCountryConfiguration, navigationIsEU } = useCountryConfigurationShim();

  const opk = sitterInfo?.opk;
  const userOpk = currentUser.user?.opk;

  const {
    areImagesLoading,
    galleryImages,
    hasMore,
    imageCount,
    isLoadingMore,
    onPaginate,
    petOwnerImageIds,
  } = useGalleryImages({
    opk,
    surface: 'sitter-profile',
    trackPersonalization: true,
  });

  const galleryPagination: PaginationConfig = useMemo(
    () => ({
      onLoadMore: onPaginate,
      hasMore,
      isLoadingMore,
      totalImageCount: imageCount,
      pageSize: GALLERY_IMAGE_PAGE_SIZE,
    }),
    [onPaginate, hasMore, isLoadingMore, imageCount]
  );

  const services = useMemo(() => sitterInfo?.services || [], [sitterInfo?.services]);
  const service = searchParams.get('service_type') || undefined;

  const [selectedServiceType, setSelectedServiceType] = useState<string | undefined>(() =>
    getDefaultServiceSlug(service, services)
  );

  useEffect(() => {
    if (service || selectedServiceType) return;
    const resolved = getDefaultServiceSlug(service, services);
    if (resolved) {
      setSelectedServiceType(resolved);
    }
  }, [service, services, selectedServiceType]);

  // Price transparency is determined per service (see DEV-150013), since each service
  // can have its own service-area location. The revamp layout renders a section per
  // service, so its selected-service UI reflects the currently selected service's level.
  const selectedServicePriceTransparencyLevel = coercePriceTransparencyLevel(
    services.find((s) => s.slug === selectedServiceType)?.priceTransparencyLevel
  );

  const legacyPriceTransparencyLevel = services.reduce<PriceTransparencyLevel>(
    (highest, s) =>
      getHigherPriceTransparencyLevel(
        highest,
        coercePriceTransparencyLevel(s.priceTransparencyLevel)
      ) ?? highest,
    PriceTransparencyLevels.none
  );
  const isPriceTransparency = getIsPriceTransparency(legacyPriceTransparencyLevel);
  const isPtCondensed = getIsPriceTransparencyCondensed(legacyPriceTransparencyLevel);

  const legacySitterProfile = useMemo(
    () =>
      sitterInfo && {
        ...sitterInfo,
        services: services.map((s) => ({
          ...s,
          priceTransparencyLevel: legacyPriceTransparencyLevel,
        })),
      },
    [sitterInfo, services, legacyPriceTransparencyLevel]
  );

  const isRevampLayout = shouldShowRevampLayout(sitterInfo);

  const {
    data: mapData,
    locationLabel: mapLocationLabel,
    isLoading: isMapLoading,
    serviceProfileType,
  } = useServiceAreaMap({
    opk,
    serviceType: selectedServiceType,
    services,
    enabled: isRevampLayout,
  });

  const { data: citySeo } = useApiPersonProfileCitySeoRetrieve(opk!, {
    service,
  });

  const { data: tractiveCode } = useDelayedTractiveDiscountCodeRetrieve({ delay: 2000 });

  const startDate = searchParams.get('start_date');
  const selectedDate = useMemo(() => {
    const startDateFormat = moment(startDate, [DF.DATE_ISO, DF.DATE_SHORT]);
    return startDate && startDateFormat.isValid() ? startDateFormat.toDate() : moment().toDate();
  }, [startDate, DF.DATE_ISO, DF.DATE_SHORT]);

  const endDate = searchParams.get('end_date');
  const selectedEndDate = useMemo(() => {
    const endDateFormat = moment(endDate, [DF.DATE_ISO, DF.DATE_SHORT]);
    return endDate && endDateFormat.isValid() ? endDateFormat.toDate() : moment().toDate();
  }, [endDate, DF.DATE_ISO, DF.DATE_SHORT]);

  const hasDateRange = !!startDate || !!endDate;

  const initialService = services.find((s) => s.slug === searchParams.get('service_type'));

  const selectedDogCount = searchParams.get('dog_count');
  const selectedCatCount = searchParams.get('cat_count');
  const selectedPuppyCount = searchParams.get('puppy_count');
  const petCounts: SearchParamsPetCounts = {
    selectedDogCount: selectedDogCount ? parseInt(selectedDogCount, 10) : 0,
    selectedCatCount: selectedCatCount ? parseInt(selectedCatCount, 10) : 0,
    selectedPuppyCount: selectedPuppyCount ? parseInt(selectedPuppyCount, 10) : 0,
  };

  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState<string>();

  const pageVariantProps = useMemo(
    () => (isRevampLayout ? { pageVariant: 'revamp' as const } : {}),
    [isRevampLayout]
  );

  useEffect(() => {
    // This event is "observability only", skips GTM, fires directly to Eventstream V3
    // and is then recorded only in Datadog
    emitMetric('sitter_profile_page_loaded');
  }, []);

  useEffect(() => {
    if (tractiveCode && tractiveCode.tractiveDiscountCode) setShowTractiveModal(true);
  }, [tractiveCode]);

  const selectedPetType: SearchParamsPetType = searchParams.get('pet_type') as SearchParamsPetType;

  const isShowingPriceInCurrency = searchParams.get('is_showing_price_in_currency') === 'true';

  const firedDefaultContentPageLoadEvent = useRef<boolean>(false);
  const firedCatOnlyContentPageLoadEvent = useRef<boolean>(false);

  const catProfile = sitterInfo?.catProviderProfile;
  const isCatOnlyProvider =
    services.length > 0 && services.every(isCatOnlyServiceBasedOnAcceptedPetTypes);
  const showCatProfileModal =
    !!(catProfile && selectedPetType !== SearchParamsPetType.CAT) &&
    !isCatOnlyBrand &&
    !isCatOnlyProvider;
  const catOnlyProfile =
    !!catProfile &&
    (selectedPetType === SearchParamsPetType.CAT || isCatOnlyBrand || isCatOnlyProvider);

  const { data: brightHorizonsBanner } = useApiBrightHorizonsSitterProfileBannerRetrieve(
    {
      serviceType: service || 'boarding',
      slug,
      petType: selectedPetType || SearchParamsPetType.DOG_CAT,
      dogCount: petCounts.selectedDogCount,
      catCount: petCounts.selectedCatCount,
      puppyCount: petCounts.selectedPuppyCount,
    },
    {
      query: {
        enabled: isBrightHorizonsUser,
      },
    }
  );

  useEffect(() => {
    if (!firedCatOnlyContentPageLoadEvent.current && catOnlyProfile) {
      fireDataLayerEvent({
        event: 'view-cat-care-content-primary',
        personId: currentUser?.user?.opk,
        sitterId: sitterInfo?.opk,
        ...pageVariantProps,
      });
      firedCatOnlyContentPageLoadEvent.current = true;
    }
  }, [catOnlyProfile, currentUser, sitterInfo?.opk, pageVariantProps]);

  useEffect(() => {
    // Intentionally firing a duplicate page-view event here:
    // we don't want to use the default page-view for ad spend optimization.
    // This event is "dataLayer only" - there's no GTM "alooma.track" tag for it.
    if (!firedDefaultContentPageLoadEvent.current) {
      fireDataLayerEvent({
        event: 'sitter-profile-view',
        personId: currentUser?.user?.opk,
        sitterId: sitterInfo?.opk,
        ...pageVariantProps,
      });
      firedDefaultContentPageLoadEvent.current = true;
    }
  }, [currentUser, sitterInfo, pageVariantProps]);

  const headerLayoutRef = useRef<HTMLDivElement | null>(null);
  const floatingFootterRef = useRef<HTMLDivElement | null>(null);

  const [headerHeight, setHeaderHeight] = useState(0);

  const handleNavigation = (e, windowDocument): void => {
    const windowEvent = e.currentTarget;
    const vh = Math.max(
      windowDocument.documentElement.clientHeight || 0,
      windowEvent.innerHeight || 0
    );
    const headerLayoutHeight = headerLayoutRef.current?.clientHeight || 0;
    if (windowEvent.scrollY > vh * 0.2 + headerLayoutHeight) {
      setIsFloatingFooterVisible(true);
    } else {
      setIsFloatingFooterVisible(false);
    }
  };

  useEffect(() => {
    if (process.env.JS_ENV_CLIENT) {
      const { addEventListener, document } = window;
      addEventListener('scroll', (e) => handleNavigation(e, document));
    }
  }, []);

  useEffect(() => {
    emitAnalyticsEvent(
      new SitterProfileGalleryImagesLoadedEvent({
        galleryImageCount: galleryImages.length,
        personId: sitterInfo?.opk,
      })
    );
  }, [galleryImages, sitterInfo?.opk]);

  useEffect(() => {
    // If the observer is undefined, exit early and don't init them
    if (typeof ResizeObserver === 'undefined') {
      return () => {};
    }

    const resizeObserver = new ResizeObserver(() => {
      if (headerLayoutRef.current) {
        setHeaderHeight(headerLayoutRef.current.clientHeight);
      }
    });

    if (process.env.JS_ENV_CLIENT && headerLayoutRef.current) {
      resizeObserver.observe(headerLayoutRef.current);
    }
    return (): void => {
      if (process.env.JS_ENV_CLIENT) {
        resizeObserver.disconnect();
      }
    };
  }, [headerLayoutRef]);

  const allGalleryImages = useMemo(() => {
    if (!sitterInfo) return [];
    const sitterImage = catOnlyProfile ? catProfile.profilePhoto : sitterInfo.defaultImage;
    return [sitterImage, ...galleryImages]
      .filter((img, index): img is Image => {
        if (!img) {
          Logger.warn('Sitter profile gallery encountered a nil image', {
            opk: sitterInfo.opk,
            source: index === 0 ? 'sitterImage' : 'galleryImage',
            index: `${index}`,
            catOnlyProfile: `${catOnlyProfile}`,
            galleryImageCount: `${galleryImages.length}`,
          });
          return false;
        }
        return true;
      })
      .map((img) => ({
        ...img,
        largeUncropped: img.largeUncroppedRetina || img.largeUncropped,
        relationship: petOwnerImageIds?.has(Number(img.id))
          ? PhotoRelationship.PetOwner
          : undefined,
      }));
  }, [sitterInfo, catOnlyProfile, catProfile, galleryImages, petOwnerImageIds]);

  const showOwnershipTags = useGateValue(ROLLOUT_STAY_MODERATION);
  const showOwnershipTagsToViewer = showOwnershipTags && !!userOpk && userOpk !== opk;

  const renderOwnershipTag = (
    images: Array<{ relationship?: PhotoRelationship }>,
    index: number
  ): ReactNode => {
    const relationship = images[index]?.relationship;
    if (!relationship) return null;

    return <OwnershipTag relationship={relationship} />;
  };

  const introVideoItem = useMemo<GalleryVideoItem | undefined>(() => {
    if (!sitterInfo?.introVideoDisplayEnabled) return undefined;
    const { introVideoUrl, introVideoThumbnail } = sitterInfo;
    if (!introVideoUrl || !introVideoThumbnail) return undefined;
    return {
      small: introVideoThumbnail,
      largeUncropped: introVideoThumbnail,
      video: {
        sources: [introVideoUrl],
        description: t`Intro video`,
      },
    };
  }, [sitterInfo]);

  const revampGalleryImages = useMemo(
    () => insertIntroVideoItem(galleryImages, introVideoItem) ?? [],
    [galleryImages, introVideoItem]
  );

  const revampLightboxImages = useMemo(
    () =>
      insertIntroVideoItem(allGalleryImages, introVideoItem, LIGHTBOX_INTRO_VIDEO_SLOT_INDEX) ?? [],
    [allGalleryImages, introVideoItem]
  );

  if (!sitterInfo) return <Box />;

  const trackImageClick = (index: number, pageVariant?: SitterProfileVersion): void => {
    emitMetric('sitter_profile_gallery_clicked', { index: `${index}` });
    Logger.info('sitter_profile_gallery_clicked', { index: `${index}` });
    emitAnalyticsEvent(
      new SitterProfileGalleryClickedEvent({
        index: `${index}`,
        inCropFix: true,
        ...(pageVariant && { pageVariant }),
      })
    );
  };

  const handleImageClick = (
    index: number,
    fromAvatar: boolean,
    pageVariant?: SitterProfileVersion
  ): void => {
    setSelectedGalleryImageIndex(index);
    setGalleryViewMode(GalleryViewMode.Lightbox);
    setIsGalleryOpen(true);
    if (fromAvatar) {
      emitMetric('sitter_profile_avatar_clicked', { index: `${index}` });
      emitAnalyticsEvent(new SitterProfileAvatarClickedEvent(pageVariantProps));
      Logger.info('sitter_profile_avatar_clicked', { index: `${index}` });
    } else {
      trackImageClick(index, pageVariant);
    }
  };

  const handleViewAllClick = (pageVariant?: SitterProfileVersion): void => {
    setGalleryViewMode(GalleryViewMode.Grid);
    setIsGalleryOpen(true);
    trackImageClick(GALLERY_PREVIEW_IMAGE_COUNT, pageVariant);
  };

  const handleViewGallery = (pageVariant?: SitterProfileVersion): void => {
    emitMetric('sitter_profile_view_gallery_clicked');
    emitAnalyticsEvent(
      new SitterProfileViewGalleryClickedEvent(pageVariant ? { pageVariant } : undefined)
    );
    Logger.info('sitter_profile_view_gallery_clicked');
    setGalleryViewMode(GalleryViewMode.Grid);
  };

  const handleGalleryImageClick = (index: number): void => {
    setSelectedGalleryImageIndex(index);
    setGalleryViewMode(GalleryViewMode.Lightbox);
  };

  const onCloseGallery = (): void => {
    setIsGalleryOpen(false);
  };

  const dismissError = (): void => {
    setError(undefined);
  };

  const dismissSuccess = (): void => {
    setSuccess(undefined);
  };

  const showError = (errorDescription: string): void => {
    setError(errorDescription);
  };

  const showSuccess = (successDescription: string): void => {
    setSuccess(successDescription);
  };

  const handleOnPetPressed = (pet: Pet): void => {
    fireDataLayerEvent({
      event: SITTER_PROFILE_EVENTSTREAM_EVENTS.CLICK_ON_SITTER_PET,
      providerId: sitterInfo.opk,
    });
    if (process.env.JS_ENV_CLIENT) {
      const { location: locationWindow } = window;
      locationWindow.href = pet.webUrl;
    }
  };

  const buildContactUrl = (serviceSlug: string | undefined): string => {
    const [contactBaseUrl, contactBaseQuery = ''] = sitterInfo.contactUrl.split('?');
    const contactSearchParams = new URLSearchParams(contactBaseQuery);
    new URLSearchParams(location.search).forEach((value, key) => {
      contactSearchParams.set(key, value);
    });
    if (serviceSlug) {
      contactSearchParams.set('service', serviceSlug);
      contactSearchParams.set('service_type', serviceSlug);
    }
    return `${contactBaseUrl}?${contactSearchParams.toString()}`;
  };

  const contactUrl = buildContactUrl(selectedServiceType);

  // Legacy contact CTA targets an approved service: keep the selected service when it
  // is approved, otherwise fall back to the first approved service. When no service is
  // approved the button is disabled.
  const approvedServices = services.filter((s) => s.isApproved);
  const hasApprovedService = approvedServices.length > 0;
  const isSelectedServiceApproved =
    !!selectedServiceType && services.some((s) => s.slug === selectedServiceType && s.isApproved);
  const legacyContactServiceType = isSelectedServiceApproved
    ? selectedServiceType
    : getDefaultServiceSlug(undefined, approvedServices);
  const legacyContactUrl = buildContactUrl(legacyContactServiceType);

  const weightLabel = (currentCountryConfiguration?.weightUnit as UnitEnum) || UnitEnum.lbs;

  const ownProfile = currentUser.user?.opk === sitterInfo.opk;
  const {
    isProfileApproved: isApproved,
    isTrainerProfileApproved,
    isProfileUnderReview: isUnderReview,
    isImportedCiafSitterWithResetIdv,
    isInLaunchedCountry: sitterInLaunchedCountry = true,
  } = sitterInfo;
  const shouldShowPromoteProfileButton =
    countryConfigData?.supportsEmpm && ownProfile && (isApproved || isTrainerProfileApproved);
  const viewerInLaunchedCountry =
    (currentCountryConfiguration?.status ?? 'launched') === 'launched';
  const isInLaunchedCountry = sitterInLaunchedCountry && viewerInLaunchedCountry;

  const Wrapper = isInLaunchedCountry ? FloatingFooter.ScrollBoundary : React.Fragment;

  const sitterLocation = sitterInfo.services[0]?.meta?.location;

  const titleSuffix = getBrandSiteName();
  const metaTitle = getBrandSpecificValue({
    rover: t`Find loving pet care with ${sitterInfo.shortName} on ${titleSuffix}`,
    catInAFlat: t`${titleSuffix} - Cat sitter, ${sitterInfo.shortName} | ${sitterLocation}`,
  });
  const metaDescription = getBrandSpecificValue({
    rover: t`Book pet sitters and dog walkers who’ll treat your pet like family.`,
    catInAFlat: t`Book cat sitters who’ll treat your cat like family.`,
  });
  const sitterImage = catOnlyProfile ? catProfile.profilePhoto : sitterInfo.defaultImage;

  const showExperience = (catOnlyProfile && catProfile.careExperience) || sitterInfo.experience;

  const mainContent = (
    <Flex
      flex="1"
      flexDirection="column"
      width={['100%', '100%', 'initial']}
      marginLeft={['0x', '0x', '6x']}
      sx={isDesktopSize === undefined ? { order: [1, 1, 0] } : {}}
    >
      {sitterInfo.providerProfile?.isActiveStarSitter && (
        <StarSitterCallout
          providerName={sitterInfo.shortName}
          isPluralAccount={sitterInfo.providerProfile.isPluralAccount}
        />
      )}
      <Box
        sx={{
          pt: sitterInfo.providerProfile?.isActiveStarSitter ? '40px' : ['0px', '0px', '4x'],
        }}
      />
      <AboutLayout>
        <Display webProps={{ as: 'h2', responsive: false }} size={displaySize} mb="6x">
          {catOnlyProfile ? catProfile.headline : services[0]?.title}
        </Display>
        {showExperience && (
          <OverflowTextModal
            title={catOnlyProfile ? t`Cat care experience` : t`Pet care experience`}
            text={catOnlyProfile ? catProfile.careExperience : sitterInfo.experience}
            onReadMore={() => {
              sendSectionInteractionEvent('care-experience');
            }}
          />
        )}
        {showCatProfileModal && (
          <CatCareExperienceContainer sitterInfo={sitterInfo} userOpk={userOpk} />
        )}
      </AboutLayout>
      <ReviewsSection opk={opk!} sitterAvatar={sitterImage.medium} />
      <SkillsAndRestDesktopLayout>
        <AboutSection info={sitterInfo} catOnlyProfile={catOnlyProfile} />
      </SkillsAndRestDesktopLayout>
    </Flex>
  );

  const servicesSidebarContent = (
    <Flex
      flexDirection="column"
      width={['100%', '100%', '352px']}
      marginRight={['0x', '0x', '6x']}
      alignItems="start"
      alignSelf="stretch"
      sx={{
        pb: ['0px', '40px', '0px'],
        order: isDesktopSize === undefined ? [2, 2, 0] : undefined,
      }}
    >
      <PricingAvailabilityLayout>
        <Flex
          flexDirection={['column', 'row', 'column']}
          alignItems={['initial', 'flex-start', 'initial']}
          width="100%"
          sx={{ pb: ['40px', '40px', '0px'] }}
        >
          <Box
            display={['block', 'flex', 'block']}
            flexDirection={['column', 'row', 'column']}
            gap={isSmDown ? '16x' : '0x'}
            width="100%"
          >
            <ServiceLayout>
              {isPriceTransparency && !isPtCondensed && !brightHorizonsBanner && (
                <SitterPriceBanner
                  countryConfigUrls={countryConfigUrls}
                  bookingFeePercentage={bookingFeePercentage}
                  petCounts={petCounts}
                  petType={selectedPetType}
                  serviceType={service}
                  services={sitterInfo?.services}
                />
              )}

              <BHSitterProfileBanner brightHorizonsBanner={brightHorizonsBanner} />

              <ServiceSection
                sitterProfile={legacySitterProfile || sitterInfo}
                petType={selectedPetType}
                petCounts={petCounts}
                selectedDate={selectedDate}
                selectedEndDate={selectedEndDate}
                hasDateRange={hasDateRange}
                priceTransparencyLevel={legacyPriceTransparencyLevel}
                isShowingPriceInCurrency={isShowingPriceInCurrency}
                bookingFeePercentage={bookingFeePercentage}
              />
            </ServiceLayout>
            <AvailabilityLayout>
              {sitterInfo.availability && (
                <Box
                  width="100%"
                  sx={{
                    paddingTop: ['40px', '0px', '40px'],
                    paddingBottom: '40px',
                  }}
                >
                  <OverflowTextModal
                    title={t`Availability`}
                    text={sitterInfo.availability}
                    onReadMore={() => {
                      sendSectionInteractionEvent('availability');
                    }}
                  />
                </Box>
              )}
              <AvailabilityCalendarSection
                locale={locale}
                sitterProfile={sitterInfo}
                selectedDate={selectedDate}
                initialService={initialService}
                isEU={navigationIsEU}
              />
            </AvailabilityLayout>
          </Box>
        </Flex>
        <Box
          borderTopWidth={['1px', '1px', '0px']}
          borderColor={theme.colors.separator.primary}
          borderTopStyle="solid"
          sx={{ pb: ['40px', '40px', '0px'] }}
        />
        <SkillsAndRestTableLayout>
          <AboutSection info={sitterInfo} catOnlyProfile={catOnlyProfile} />
        </SkillsAndRestTableLayout>
      </PricingAvailabilityLayout>
      <Flex flexDirection={['column', 'row', 'column']} alignItems="flex-start" alignSelf="stretch">
        {sitterInfo.googleMapsUri && (
          <LocationLayout>
            <Heading size="300" mb="2x">
              {t`Location`}
            </Heading>
            <Paragraph size="200" data-testid="sitter-location">
              {sitterLocation}
            </Paragraph>
            <StaticLocation url={sitterInfo.googleMapsUri} sitterName={sitterInfo.shortName} />
          </LocationLayout>
        )}
        {sitterInfo.pets.length > 0 && (
          <PetsLayout>
            <Heading size="300">{t`Pets`}</Heading>
            {sitterInfo.pets.map((pet, i) => (
              <Box key={i} pt={i === 0 ? '0x' : '6x'} width="100%">
                <PetBlock
                  pet={pet}
                  weightLabel={weightLabel}
                  onPetPressed={handleOnPetPressed}
                  dataTestId={pet.name}
                />
              </Box>
            ))}
          </PetsLayout>
        )}
      </Flex>
      {currentUser.user?.opk !== sitterInfo.opk && (
        <Flex sx={{ paddingY: '40px' }} width="100%">
          <ReportProfileSection
            isLoggedIn={currentUser.isAuthenticated}
            location={location}
            onSuccess={showSuccess}
          />
        </Flex>
      )}
    </Flex>
  );

  /**
   * Conditional rendering for accessibility:
   *  - Desktop renders Services sidebar first for proper left-to-right tab order,
   *  - Mobile web renders main content first for better UX
   */
  const content = (() => {
    if (isDesktopSize === undefined) {
      return [servicesSidebarContent, mainContent];
    }

    return isDesktopSize
      ? [servicesSidebarContent, mainContent]
      : [mainContent, servicesSidebarContent];
  })();

  const context = sitterInfo?.authenticatedContext;

  if (isRevampLayout) {
    const viewMode: ViewMode = {
      petType: catOnlyProfile || isCatOnlyBrand ? 'cat-only' : 'mixed',
      viewer: ownProfile ? 'self' : 'default',
    };

    return (
      <RevampProfileProvider
        serviceType={selectedServiceType}
        setServiceType={setSelectedServiceType}
        sitterData={sitterInfo}
        viewMode={viewMode}
        pageTreatment={sitterInfo.sitterProfileVersion as SitterProfileVersion}
        contactUrl={contactUrl}
        isShowingPriceInCurrency={isShowingPriceInCurrency}
      >
        <Fragment>
          <Helmet>
            <meta property="og:title" content={metaTitle} />
            <meta property="og:image" content={sitterImage.wideRetina} />
            <meta property="og:description" content={metaDescription} />
            <meta name="twitter:image" content={sitterImage.wideRetina} />
            <title>{metaTitle}</title>
            {sitterInfo.canonicalUrl && <link rel="canonical" href={sitterInfo.canonicalUrl} />}
          </Helmet>
          <Flex
            flexDirection="column"
            alignItems="center"
            width="100%"
            alignSelf="stretch"
            sx={{ overflowX: ['hidden', 'hidden', 'unset'] }}
            webProps={
              enableSitterProfileRichResults
                ? {
                    itemScope: true,
                    itemType: schema`LocalBusiness`,
                  }
                : undefined
            }
          >
            {enableSitterProfileRichResults && (
              <SitterProfileLocalBusinessSchema
                url={sitterInfo.canonicalUrl ?? sitterInfo.webUrl}
                imageUrl={sitterImage.wideRetina}
                locale={locale}
                services={services}
                isCatOnly={isCatOnlyBrand}
              />
            )}
            <Section width="100%" contentWidth="page">
              <SitterProfilePageLayout>
                <Box width="100%" data-testid="sticky-profile-header">
                  <RevampProfileHeader
                    sitterInfo={sitterInfo}
                    sitterImage={sitterImage.medium}
                    ownProfile={ownProfile}
                    isApproved={isApproved}
                    isUnderReview={isUnderReview}
                    isImportedCiafSitterWithResetIdv={isImportedCiafSitterWithResetIdv}
                    onAvatarClick={(index) => handleImageClick(index, true)}
                    isInLaunchedCountry={isInLaunchedCountry}
                    onError={showError}
                    location={location}
                    service={service}
                    serviceType={selectedServiceType}
                    searchParams={searchParams}
                    isLoggedIn={currentUser.isAuthenticated}
                    contactUrl={contactUrl}
                    sitterSlug={slug}
                    shouldShowPromoteProfileButton={shouldShowPromoteProfileButton}
                  />
                  <HeaderLayout ref={headerLayoutRef}>
                    <GalleryBlock
                      images={revampGalleryImages}
                      imageCount={imageCount}
                      onImagePress={(index) =>
                        handleImageClick(
                          index + DEFAULT_IMAGE_OFFSET,
                          false,
                          SitterProfileVersionOptions.REVAMP
                        )
                      }
                      onViewAllPress={() => handleViewAllClick(SitterProfileVersionOptions.REVAMP)}
                      hideOverlay={areImagesLoading}
                      mapData={mapData}
                      serviceProfileType={serviceProfileType}
                      locationLabel={mapLocationLabel}
                      isMapLoading={isMapLoading}
                      serviceType={selectedServiceType}
                    />
                  </HeaderLayout>
                </Box>
                {isGalleryOpen && (
                  <ImageGallery
                    images={revampLightboxImages}
                    galleryViewMode={galleryViewMode}
                    lightboxIndex={selectedGalleryImageIndex}
                    setLightboxIndex={setSelectedGalleryImageIndex}
                    onClose={onCloseGallery}
                    onImageClick={handleGalleryImageClick}
                    onViewGallery={handleViewGallery}
                    pagination={galleryPagination}
                    renderImageOverlay={
                      showOwnershipTagsToViewer
                        ? (index) => renderOwnershipTag(revampLightboxImages, index)
                        : undefined
                    }
                  />
                )}
                <RevampPageContent
                  selectedPetType={selectedPetType}
                  petCounts={petCounts}
                  selectedDate={selectedDate}
                  selectedEndDate={selectedEndDate}
                  hasDateRange={hasDateRange}
                  priceTransparencyLevel={selectedServicePriceTransparencyLevel}
                  isShowingPriceInCurrency={isShowingPriceInCurrency}
                  bookingFeePercentage={bookingFeePercentage}
                  locale={locale}
                  navigationIsEU={navigationIsEU}
                  location={location}
                  opk={opk!}
                  sitterAvatar={sitterImage.medium}
                  isApproved={isApproved}
                />
              </SitterProfilePageLayout>
            </Section>
          </Flex>
          <MoreSittersLayout>
            <MemberProfileSignal />
            <MoreSitters
              sitterOpk={opk!}
              service={initialService?.slug}
              searchParams={searchParams.toString()}
            />
            <SeoBreadcrumbs citySeoName={citySeo?.citySeoName} citySeoUrl={citySeo?.citySeoUrl} />
          </MoreSittersLayout>
          <RevampMobileCtaBar
            priceTransparencyLevel={selectedServicePriceTransparencyLevel}
            locale={locale}
            petCount={petCounts}
            petType={selectedPetType}
            bookingFeePercentage={bookingFeePercentage}
            isApproved={isApproved}
          />
          <DjangoMessages />
          <SmartCollarModal
            discountCode={tractiveCode?.tractiveDiscountCode ?? ''}
            open={showTractiveModal}
            onClose={() => setShowTractiveModal(false)}
            entrypoint={SmartCollarEntrypoint.SITTER_PROFILE}
          />
        </Fragment>
      </RevampProfileProvider>
    );
  }

  return (
    <Fragment>
      <Helmet>
        <meta property="og:title" content={metaTitle} />
        <meta property="og:image" content={sitterImage.wideRetina} />
        <meta property="og:description" content={metaDescription} />
        <meta name="twitter:image" content={sitterImage.wideRetina} />
        <title>{metaTitle}</title>
        {sitterInfo.canonicalUrl && <link rel="canonical" href={sitterInfo.canonicalUrl} />}
      </Helmet>
      <Wrapper offsetTop={`calc(100vh + ${headerHeight}px)`}>
        <Flex
          flexDirection="column"
          alignItems="center"
          width="100%"
          pt="8x"
          alignSelf="stretch"
          webProps={
            enableSitterProfileRichResults
              ? {
                  itemScope: true,
                  itemType: schema`LocalBusiness`,
                }
              : undefined
          }
        >
          {enableSitterProfileRichResults && (
            <SitterProfileLocalBusinessSchema
              url={sitterInfo.canonicalUrl ?? sitterInfo.webUrl}
              imageUrl={sitterImage.wideRetina}
              locale={locale}
              services={services}
              isCatOnly={isCatOnlyBrand}
            />
          )}
          <Section
            sx={{
              marginTop: '0',
              marginBottom: '40px',
              maxWidth: [MOBILE_MAX_WIDTH_LAYOUT, 'initial', 'initial'],
            }}
          >
            <SitterProfilePageLayout>
              <HeaderLayout ref={headerLayoutRef}>
                <ProfileLayout>
                  <SitterInformation
                    sitter={sitterInfo}
                    sitterImage={sitterImage.medium}
                    ownProfile={ownProfile}
                    isApproved={isApproved}
                    isUnderReview={isUnderReview}
                    isImportedCiafSitterWithResetIdv={isImportedCiafSitterWithResetIdv}
                    onAvatarClick={(index) => handleImageClick(index, true)}
                    isInLaunchedCountry={isInLaunchedCountry}
                    onError={showError}
                    location={location}
                    enableRichResults={enableSitterProfileRichResults}
                  />
                  {!ownProfile && isApproved && isInLaunchedCountry && (
                    <ContactButton
                      sitterInfo={sitterInfo}
                      contactUrl={legacyContactUrl}
                      sitterSlug={slug}
                      isLoggedIn={currentUser.isAuthenticated}
                      location={location}
                      pagePosition="main"
                      mb="4x"
                      disabled={!hasApprovedService}
                    />
                  )}
                  {!ownProfile && (
                    <BackToSearchButton serviceType={service} searchParams={searchParams} />
                  )}
                  {shouldShowPromoteProfileButton ? <PromoteProfileButton fullWidth /> : null}
                </ProfileLayout>
                <GalleryLayout isInSitterCropFix>
                  {galleryImages.length > 0 && (
                    <PhotoGallery
                      images={galleryImages}
                      onImageClick={(index) => handleImageClick(index, false)}
                      onViewAllClick={handleViewAllClick}
                      count={imageCount}
                      hideOverlay={areImagesLoading}
                      isInSitterGalleryCropFixExperiment
                    />
                  )}
                </GalleryLayout>
              </HeaderLayout>
              {isGalleryOpen && (
                <ImageGallery
                  images={allGalleryImages}
                  galleryViewMode={galleryViewMode}
                  lightboxIndex={selectedGalleryImageIndex}
                  setLightboxIndex={setSelectedGalleryImageIndex}
                  onClose={onCloseGallery}
                  onImageClick={handleGalleryImageClick}
                  onViewGallery={handleViewGallery}
                  pagination={galleryPagination}
                  renderImageOverlay={
                    showOwnershipTagsToViewer
                      ? (index) => renderOwnershipTag(allGalleryImages, index)
                      : undefined
                  }
                />
              )}
            </SitterProfilePageLayout>
            <Tabs showReviewsTab={sitterInfo.reviewsCount > 0} />
            <SitterProfilePageLayout>
              <Flex
                flexDirection={['column', 'column', 'row']}
                sx={{ pt: ['40px', '0px', '80px'], gap: ['0px', '0px', '24px'] }}
              >
                {content}
              </Flex>
            </SitterProfilePageLayout>
          </Section>
          {(error || success) && (
            <Flex
              zIndex="99999"
              position="fixed"
              bottom={`${
                (isFloatingFooterVisible
                  ? (floatingFootterRef?.current?.clientHeight || 0) + FLOATING_FOOTER_PADDING
                  : 0) + 40
              }px`}
              left={['none', 'none', '40px']}
              textAlign="center"
              justifyContent="center"
            >
              {error && (
                <Banner
                  severity="danger"
                  hideIcon
                  onClose={dismissError}
                  containerProps={{ mb: '0x' }}
                >
                  {error}
                </Banner>
              )}
              {success && (
                <Banner
                  severity="success"
                  hideIcon
                  onClose={dismissSuccess}
                  containerProps={{ mb: '0x' }}
                >
                  {success}
                </Banner>
              )}
            </Flex>
          )}
        </Flex>
        <MoreSittersLayout>
          <MemberProfileSignal />
          <MoreSitters
            sitterOpk={opk!}
            service={initialService?.slug}
            searchParams={searchParams.toString()}
          />
          <SeoBreadcrumbs citySeoName={citySeo?.citySeoName} citySeoUrl={citySeo?.citySeoUrl} />
        </MoreSittersLayout>
        {isInLaunchedCountry && (
          <FloatingFooter background="primary">
            <Flex maxWidth={['100%']} justifyContent="center" ref={floatingFootterRef}>
              <Flex
                width="100%"
                justifyContent={['center', 'space-between']}
                alignItems="center"
                maxWidth={[MOBILE_MAX_WIDTH_LAYOUT, 'initial']}
              >
                <Box display={['none', 'flex']} flex={['none', '1']}>
                  <SitterInformation
                    sitter={sitterInfo}
                    sitterImage={sitterImage.medium}
                    ownProfile={ownProfile}
                    isApproved={isApproved}
                    isUnderReview={isUnderReview}
                    isImportedCiafSitterWithResetIdv={isImportedCiafSitterWithResetIdv}
                    variant="compact"
                  />
                </Box>
                <Flex width={['100%', 'auto']}>
                  <Flex
                    flexDirection={['column-reverse', 'row']}
                    alignItems="center"
                    justifyContent="left"
                    width="100%"
                  >
                    {!ownProfile && (
                      <BackToSearchButton serviceType={service} searchParams={searchParams} />
                    )}
                    {!ownProfile && isApproved && context?.canContact !== false && (
                      <ContactButton
                        sitterInfo={sitterInfo}
                        contactUrl={legacyContactUrl}
                        sitterSlug={slug}
                        isLoggedIn={currentUser.isAuthenticated}
                        location={location}
                        pagePosition="sticky-footer"
                        mr={['0x', '4x']}
                        mb={['2x', '0x']}
                        disabled={!hasApprovedService}
                      />
                    )}
                    {!ownProfile &&
                      isApproved &&
                      context?.canFavorite !== false &&
                      !isShortlistVisible && (
                        <Box display={['none', 'block']}>
                          <FavoriteButton
                            sitterInfo={sitterInfo}
                            isLoggedIn={currentUser.isAuthenticated}
                            onError={showError}
                            location={location}
                          />
                        </Box>
                      )}
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </FloatingFooter>
        )}
        <DjangoMessages />
        <SmartCollarModal
          discountCode={tractiveCode?.tractiveDiscountCode ?? ''}
          open={showTractiveModal}
          onClose={() => setShowTractiveModal(false)}
          entrypoint={SmartCollarEntrypoint.SITTER_PROFILE}
        />
      </Wrapper>
    </Fragment>
  );
};

SitterProfilePage.reactQueryPrefetch = sitterProfileDataPrefetch;
export default SitterProfilePage;
